import { useCallback, useMemo } from 'react';
import { Route, RouterLink } from '@bymason-private/route-provider';
import { GroupDeploymentsModel, ArtifactType } from '@bymason/controller-api-hooks';
import {
  Box,
  ControllerDataGrid,
  GridColDef,
  GridRenderCellParams,
  dataGridSemverCompare,
  DataGridProProps,
  GridTreeDataGroupingCell,
  GridGroupingColDefOverride,
  GridOverlay,
  Link,
} from '@bymason-private/legacy-pattern-components';
import { formatStringToTitleCase, formatLongDateTimeString } from '@bymason/utils';

import { DEPLOY_TYPE_MAP } from '../../app-constants';
import { ArtifactTypeIcon } from '../artifact-type-icon';

const GROUPING_ID_PREFIX = '__recent__';
const DEPLOYMENT_TYPE_ORDER = ['config', 'ota', 'apk'];

export function DeploymentsList({
  deployments,
  deployType,
  isBusy,
  isGrouped,
  linkConfig,
  noDataMessage,
  noRowsMessage,
  projectViewRoute,
}: DeploymentsListProps) {
  const NameRender = useCallback(
    (params: GridRenderCellParams) => {
      const type = params.row['type'] as string;
      const version = params.row['version'] as string;
      const name = params.row['name'] as string;
      const projectRoute = projectViewRoute && { ...projectViewRoute, path: `/projects/:name#${version}` };

      return linkConfig && type === ArtifactType.Config ? (
        projectRoute ? (
          <RouterLink
            linkComponent={<Link className='MuiDataGrid-cellContent'>{name}</Link>}
            route={projectRoute}
            state={{ name }}
          />
        ) : (
          <span>{name}</span>
        )
      ) : (
        <span className='MuiDataGrid-cellContent'>{name}</span>
      );
    },
    [linkConfig, projectViewRoute]
  );

  const VersionRender = (params: GridRenderCellParams) => (
    <span className='MuiDataGrid-cellContent' title={String(params.value || '')}>
      {params.value}
    </span>
  );

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'name',
        headerName: 'Name',
        minWidth: 200,
        flex: 3,
        renderCell: NameRender,
        hide: isGrouped,
      },
      {
        field: 'version',
        headerName: 'Version',
        minWidth: 75,
        flex: 1,
        sortComparator: dataGridSemverCompare,
        renderCell: VersionRender,
      },
      { field: 'type', headerName: 'Type' },
      {
        field: 'date',
        headerName: 'Date deployed',
        type: 'date',
        valueFormatter: (params) => {
          return params.value ? formatLongDateTimeString(params.value as Date) : '';
        },
        minWidth: 280,
        width: 300,
      },
    ],
    [isGrouped, NameRender]
  );

  const groupingColDef: GridGroupingColDefOverride = {
    headerName: deployType ? `${formatStringToTitleCase(DEPLOY_TYPE_MAP.get(deployType) || 'Name')}` : 'Name',
    minWidth: 200,
    flex: 3,
    // eslint-disable-next-line react/display-name
    renderCell: (params) => {
      const rowId = params.id as string;
      const type = params.row.type;
      if (!params.rowNode.isAutoGenerated && !rowId.startsWith(GROUPING_ID_PREFIX)) {
        return <GridTreeDataGroupingCell {...params} formattedValue={<NameRender {...params} />} />;
      }

      return (
        <Box
          sx={{
            '& .MuiDataGrid-treeDataGroupingCell > span': {
              display: 'flex',
              columnGap: 0.5,
            },
          }}
        >
          <GridTreeDataGroupingCell
            {...params}
            formattedValue={
              <>
                <ArtifactTypeIcon type={type} fontSize={20} />
                <strong>{<NameRender {...params} />}</strong>
              </>
            }
          />
        </Box>
      );
    },
  };

  const rows: RowData[] = useMemo(() => {
    return (deployments || [])
      .filter((dep) =>
        deployType ? deployType === dep.type : DEPLOYMENT_TYPE_ORDER.some((t) => t === dep.type)
      )
      .sort((a, b) => b.createdAt.localeCompare(a.createdAt))
      .map((dep) => ({
        id: dep.uuid,
        type: dep.type,
        name: dep.name,
        date: Date.parse(dep.createdAt),
        version: dep.version,
        hierarchy: [dep.name, dep.uuid],
      }));
  }, [deployments, deployType]);

  const groupings = useMemo(() => {
    const recentMap = rows.reduce<Record<string, RowData>>((acc, row) => {
      const aggKey = `${row.type}-${row.name}`;
      const prevTypeRow = acc[aggKey];
      const groupingId = `${GROUPING_ID_PREFIX}${aggKey}`;

      if (!prevTypeRow || prevTypeRow.date < row.date) {
        acc[aggKey] = {
          id: groupingId,
          date: row.date,
          name: row.name,
          type: row.type,
          version: row.version,
          hierarchy: [row.name],
        };
      }

      return acc;
    }, {});

    return Object.values(recentMap);
  }, [rows]);

  const getTreeDataPath: DataGridProProps['getTreeDataPath'] = (row) => row.hierarchy;

  return (
    <Box mt={1.5} mb={1.5}>
      {deployments && deployments?.length ? (
        <Box width='100%'>
          {!isBusy && (
            <ControllerDataGrid
              initialState={{
                sorting: {
                  sortModel: [{ field: 'date', sort: 'desc' }],
                },
              }}
              rows={isGrouped ? [...groupings, ...rows] : rows}
              columns={columns}
              disableColumnResize={false}
              treeData={isGrouped}
              groupingColDef={groupingColDef}
              getTreeDataPath={getTreeDataPath}
              disableSelectionOnClick
              loading={isBusy}
              components={{
                // eslint-disable-next-line react/display-name
                NoRowsOverlay: () => (
                  <GridOverlay sx={{ bgcolor: 'transparent' }}>
                    {noRowsMessage || 'No deployments'}
                  </GridOverlay>
                ),
              }}
            />
          )}
        </Box>
      ) : (
        !isBusy && noDataMessage
      )}
    </Box>
  );
}

export interface DeploymentsListProps {
  deployments?: GroupDeploymentsModel[] | void;
  deployType?: ArtifactType;
  isBusy?: boolean;
  isGrouped?: boolean;
  linkConfig?: boolean;
  noDataMessage?: JSX.Element;
  noRowsMessage?: JSX.Element;
  projectViewRoute?: Route;
}

interface RowData {
  id: string;
  date: number;
  type: ArtifactType;
  version: string;
  name: string;
  hierarchy: string[];
}
